const EVENTS = {
    ADD_TO_CART: 'track-add-to-cart',
    REMOVE_FROM_CART: 'track-remove-from-cart',
    CHECKOUT: 'track-checkout',
    PRODUCT: 'track-product',
    DRAWING_DOWNLOAD: 'track-drawing-download',
    LIVE_CHAT: 'track-livechat',
    SEARCH: 'track-search',
    CATEGORY_PAGE: 'track-category-page',
    COOKIES_ACCEPTED: 'cookies-accepted',
    USER_LOADED: 'user-loaded'
}

const klaviyo = ({ apiKey }) => {
    let isScriptLoaded = false
    let isUserIdentified = false
    return {
        init() {
            if (!this.hasCookieConsent()) return
            window._learnq = window._learnq || []
            this.loadKlaviyoScript(apiKey)
            this.setupListeners()
            if (!this.hasCookieConsent()) return
            this.startTracking()
        },
        startTracking() {
            window._learnq = window._learnq || []
            this.loadKlaviyoScript(apiKey)
            this.identifyUserOnLoad()
        },
        loadKlaviyoScript(apiKey) {
            if (!apiKey || isScriptLoaded) return
            if (document.querySelector('script[data-klaviyo]')) {
                isScriptLoaded = true
                return
            }
            const script = document.createElement('script')
            script.src = `https://static.klaviyo.com/onsite/js/${apiKey}/klaviyo.js`
            script.defer = true
            script.setAttribute('data-klaviyo', 'true')
            document.head.appendChild(script)
            isScriptLoaded = true
            script.onload = () => { isScriptLoaded = true }
            script.onerror = () => { isScriptLoaded = false }
        },
        setupListeners() {
            const listeners = [
                [EVENTS.ADD_TO_CART, e => this.trackAddToCart(e.detail.product, e.detail.quantity)],
                [EVENTS.REMOVE_FROM_CART, e => this.trackRemoveFromCart(e.detail.product)],
                [EVENTS.CHECKOUT, e => this.trackCheckout(e.detail.cart)],
                [EVENTS.PRODUCT, e => this.trackProduct(e.detail.product)],
                [EVENTS.DRAWING_DOWNLOAD, e => this.trackDownloadDrawing(e.detail.productId, e.detail.modelId)],
                [EVENTS.LIVE_CHAT, () => this.trackLiveChatOpened()],
                [EVENTS.SEARCH, e => this.trackSearch(e.detail.query)],
                [EVENTS.CATEGORY_PAGE, () => this.trackCategoryPage()],
                [EVENTS.COOKIES_ACCEPTED, () => this.startTracking()],
                // The user store loads from /api/user after this component starts
                // (e.g. the first page after logging in), so identify when it lands.
                [EVENTS.USER_LOADED, () => this.identifyUserOnLoad()]
            ]
            listeners.forEach(([event, handler]) => window.addEventListener(event, handler))
            return () => listeners.forEach(([event, handler]) => window.removeEventListener(event, handler))
        },
        hasCookieConsent() {
            try {
                const cookies = JSON.parse(localStorage.getItem(getFernKey('cookies')))
                const analyticsConsent = cookies?.options?.find(opt => opt.name === 'analytics')?.value === true
                return cookies?.consent === true && analyticsConsent
            } catch {
                return false
            }
        },
        isUser() {
            if (typeof Alpine === 'undefined') return false
            return typeof Alpine.store === 'function' &&
                Alpine.store('user') &&
                Alpine.store('user').email
        },
        getUserData() {
            const user = Alpine.store('user')
            return {
                '$email': user.email,
                '$first_name': user.firstname,
                '$last_name': user.lastname,
                customer_id: user.id_customer,
                language: user.language_iso_code
            }
        },
        identifyUserOnLoad() {
            if (!this.isUser() || isUserIdentified || !window._learnq) return
            const userData = this.getUserData()
            const isNewAccount = new URLSearchParams(window.location.search).get('new_account') === '1'
            window._learnq.push(['identify', userData])
            if (isNewAccount) {
                window._learnq.push(['track', 'Account Created', userData])
            }
            isUserIdentified = true
        },
        trackAddToCart(product, quantity) {
            if (!product || !window._learnq) return
            const id = product.id_product || product.id || product.objectID
            if (!id) return
            const price = typeof product.price === 'string' ? parseFloat(product.price) : product.price
            if (isNaN(price)) return
            const qty = quantity ?? product.quantity ?? 1
            if (!Number.isInteger(qty)) return
            const cat = product.id_category_default || product.categories || null
            const eventType = qty === 0 ? 'Removed from Cart' : 'Added to Cart'
            window._learnq.push(['track', eventType, {
                ProductName: product.name,
                ProductID: id,
                SKU: product.reference,
                Categories: cat,
                ImageURL: product.image_url || product.image || '',
                URL: window.location.href,
                Brand: product.brand || '',
                Price: price,
                Quantity: qty
            }])
        },
        trackRemoveFromCart(product) {
            if (!product || !window._learnq) return;

            const id = product.id_product || product.id || product.objectID;
            if (!id) return;

            const price = typeof product.price === 'string' ? parseFloat(product.price) : product.price;
            const quantity = product.quantity ?? 0;

            window._learnq.push(['track', 'Removed from Cart', {
                ProductID: id,
                ProductName: product.name,
                SKU: product.reference,
                Price: price,
                Quantity: quantity,
                Categories: product.categories || product.id_category_default,
                ImageURL: product.image_url || product.image_path || '',
                URL: product.link || window.location.href
            }]);
        },
        trackProduct(product) {
            if (!product || !window._learnq) return
            const id = product.id_product || product.id || product.objectID
            if (!id) return
            const price = typeof product.price === 'string' ? parseFloat(product.price) : product.price
            if (isNaN(price)) return
            window._learnq.push(['track', 'Viewed Product', {
                ProductName: product.name,
                ProductID: id,
                SKU: product.reference,
                Categories: product.categories || product.id_category_default,
                URL: window.location.href,
                Brand: product.brand || '',
                Price: price
            }])
        },
        trackLiveChatOpened() {
            if (!window._learnq) return
            window._learnq.push(['track', 'Live Chat Opened', {
                URL: window.location.href,
                PageTitle: document.title
            }])
        },
        trackCheckout(cart) {
            if (!cart || !cart.products || !window._learnq) return
            window._learnq.push(['track', 'Started Checkout', {
                Items: cart.products.map(p => {
                    const id = p.id_product || p.id || p.objectID
                    const price = typeof p.price === 'string' ? parseFloat(p.price) : p.price
                    if (!id || isNaN(price)) return null
                    return {
                        ProductID: id,
                        ProductName: p.name,
                        Quantity: p.quantity,
                        ItemPrice: price
                    }
                }).filter(Boolean),
                Total: cart.order_total_taxed_no_shipping,
                Shipping: cart.shipping_cost,
                VAT: cart.tax_difference,
                Currency: cart.currency,
                URL: window.location.href
            }])
        },
        trackDownloadDrawing(productId, modelId) {
            if (!productId || !modelId || !window._learnq) return
            window._learnq.push(['track', 'Download Drawing', {
                ProductID: productId,
                ModelID: modelId,
                URL: window.location.href,
                PageTitle: document.title
            }])
        },
        trackSearch(query) {
            if (!window._learnq || !query) return
            window._learnq.push(['track', 'Search', {
                Query: query,
                URL: window.location.href,
                PageTitle: document.title
            }])
        },
        trackCategoryPage() {
            if (!window._learnq) return
            window._learnq.push(['track', 'Viewed Category', {
                URL: window.location.href,
                PageTitle: document.title
            }])
        }
    }
}

document.addEventListener('alpine:init', () => {
    window.Alpine.data('klaviyo', klaviyo)
})
